import { useState, useContext, useEffect, useCallback, useMemo } from "react";
import '../../styles/page-titles.css'
import PermissionGate from "../../services/PermissionGate";
import styled from 'styled-components';
import { SidebarContext } from "../../AppRoutes";
import { getRelatorioEstoque } from "../../services/api";
import { nowTz } from '../../utils/timezone';
import Loading from "react-loading";
import {
    FaWarehouse, FaExclamationTriangle, FaTimesCircle, FaBoxOpen,
    FaArrowDown, FaArrowUp, FaSyncAlt, FaClipboardList
} from 'react-icons/fa';
import { MdTrendingDown, MdInventory, MdReceipt } from 'react-icons/md';
import { IoNutrition } from 'react-icons/io5';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell, Legend, AreaChart, Area
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { formatPriceWithSymbol } from '../../utils/currency';

const Container = styled.div`
    display: flex;
    flex-direction: column;
    margin-left: ${({ sidebar }) => (sidebar ? '250px' : '100px')};
    min-height: 100vh;
    width: auto;
    transition: margin-left 200ms ease;
    background-color: #ffffff;
    padding: 24px;

    @media (max-width: 880px) {
        margin-left: 0;
        padding: 16px;
    }
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 24px;
    flex-wrap: wrap;
    gap: 16px;

    .filters {
        display: flex;
        gap: 12px;
        flex-wrap: wrap;
    }

    input[type="date"] {
        padding: 8px 12px;
        border: 1px solid #ddd;
        border-radius: 8px;
        font-size: 14px;
    }

    button {
        padding: 8px 16px;
        background: linear-gradient(135deg, rgb(49, 140, 213), rgb(79, 172, 254));
        color: white;
        border: none;
        border-radius: 8px;
        cursor: pointer;
        font-weight: 500;
        transition: opacity 0.2s;

        &:hover {
            opacity: 0.9;
        }

        &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
        }
    }

    @media (max-width: 600px) {
        h1 { font-size: 18px; }
        .filters { width: 100%; }
        input[type="date"], button { flex: 1; }
    }
`;

const CardsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 20px;
    margin-bottom: 30px;

    @media (max-width: 500px) {
        grid-template-columns: repeat(2, 1fr);
        gap: 12px;
    }
`;

const MetricCard = styled.div`
    background: white;
    padding: 20px;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    display: flex;
    flex-direction: column;
    gap: 8px;

    .icon {
        width: 40px;
        height: 40px;
        border-radius: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 20px;
        margin-bottom: 8px;
    }

    .icon.blue { background: rgba(49, 140, 213, 0.1); color: rgb(49, 140, 213); }
    .icon.green { background: rgba(40, 167, 69, 0.1); color: #28a745; }
    .icon.orange { background: rgba(255, 152, 0, 0.1); color: #ff9800; }
    .icon.red { background: rgba(220, 53, 69, 0.1); color: #dc3545; }
    .icon.purple { background: rgba(102, 126, 234, 0.1); color: #667eea; }

    .label {
        font-size: 14px;
        color: #666;
    }

    .value {
        font-size: 28px;
        font-weight: 700;
        color: #333;
    }

    .sub {
        font-size: 12px;
        color: #999;
    }

    @media (max-width: 500px) {
        padding: 14px;
        .value { font-size: 22px; }
    }
`;

const ChartsRow = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    margin-bottom: 30px;

    @media (max-width: 1000px) {
        grid-template-columns: 1fr;
    }
`;

const ChartCard = styled.div`
    background: white;
    padding: 24px;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

    h3 {
        font-size: 16px;
        font-weight: 600;
        color: #333;
        margin-bottom: 20px;
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .chart-container {
        height: 300px;
        width: 100%;
    }

    @media (max-width: 600px) {
        padding: 16px;
        .chart-container { height: 250px; }
    }
`;

const FullWidthChart = styled(ChartCard)`
    grid-column: 1 / -1;
    .chart-container { height: 350px; }
`;

const DataTable = styled.div`
    background: white;
    border-radius: 16px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    overflow: hidden;
    margin-bottom: 20px;

    .table-header {
        padding: 20px 24px;
        border-bottom: 1px solid #e2e8f0;

        h3 {
            font-size: 18px;
            font-weight: 600;
            color: #2d3748;
            margin: 0;
            display: flex;
            align-items: center;
            gap: 10px;
        }
    }

    .table-wrapper {
        overflow-x: auto;
    }

    table {
        width: 100%;
        border-collapse: collapse;

        thead {
            background: linear-gradient(135deg, rgb(49, 140, 213), rgb(79, 172, 254));

            th {
                padding: 16px;
                text-align: left;
                font-weight: 600;
                font-size: 13px;
                color: white;
                white-space: nowrap;

                &:first-child { padding-left: 24px; }
                &:last-child { padding-right: 24px; }
            }
        }

        tbody {
            tr {
                border-bottom: 1px solid #f1f5f9;
                transition: background-color 0.2s ease;

                &:hover { background: #f8fafc; }

                td {
                    padding: 14px 16px;
                    color: #2d3748;
                    font-size: 14px;

                    &:first-child { padding-left: 24px; }

                    .status-badge {
                        display: inline-flex;
                        padding: 4px 10px;
                        border-radius: 20px;
                        font-size: 12px;
                        font-weight: 600;

                        &.danger { background: #f8d7da; color: #721c24; }
                        &.warning { background: #fff3cd; color: #856404; }
                        &.success { background: #d4edda; color: #155724; }
                    }

                    .valor { font-weight: 600; color: rgb(49, 140, 213); }
                }
            }
        }
    }

    .empty-state {
        text-align: center;
        padding: 40px 20px;
        color: #a0aec0;

        svg { font-size: 48px; margin-bottom: 16px; opacity: 0.5; }
        p { margin: 0; font-size: 14px; }
    }

    @media (max-width: 768px) {
        table { min-width: 600px; }
    }
`;

const LoadingContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 300px;
`;

const COLORS = ['#318CD5', '#28a745', '#ff9800', '#dc3545', '#667eea', '#4FACFE'];
const MOVEMENT_COLORS = {
    entradas: '#28a745',
    saidas: '#dc3545',
    saidas_automaticas: '#ff9800',
    ajustes: '#667eea'
};
const ORIGIN_COLORS = {
    itens: '#318CD5',
    materias_primas: '#28a745',
    adicionais: '#667eea'
};

const RelatorioEstoque = () => {
    const { sidebar } = useContext(SidebarContext);
    const { t } = useTranslation('reports');

    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState({
        resumo: {},
        movimentacoes: {},
        movimentacoes_por_origem: {},
        evolucao: [],
        top_consumidas: [],
        alertas: [],
        custos_por_item: [],
        distribuicao_unidade: [],
        itens_esgotados: []
    });
    const [startDate, setStartDate] = useState(nowTz().subtract(30, 'days').format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState(nowTz().format('YYYY-MM-DD'));

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await getRelatorioEstoque(startDate, endDate);
            if (response?.data?.success) {
                setData(response.data);
            }
        } catch (error) {
            console.error('[RELATORIO-ESTOQUE] Erro ao buscar dados:', error);
            toast.error(t('common.fetchError'));
        } finally {
            setIsLoading(false);
        }
    }, [startDate, endDate, t]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Dados para gráfico de pizza de movimentações
    const movPieData = useMemo(() => {
        const mov = data.movimentacoes || {};
        return [
            { name: t('stock.entries'), value: mov.entradas || 0 },
            { name: t('stock.exits'), value: mov.saidas || 0 },
            { name: t('stock.autoExits'), value: mov.saidas_automaticas || 0 },
            { name: t('stock.adjustments'), value: mov.ajustes || 0 }
        ].filter(item => item.value > 0);
    }, [data.movimentacoes, t]);

    // Dados para gráfico de evolução
    const evolucaoData = useMemo(() => {
        return (data.evolucao || []).map(item => ({
            ...item,
            data: item.data ? item.data.substring(5) : '' // MM-DD
        }));
    }, [data.evolucao]);

    // Dados para gráfico de distribuição por unidade
    const unidadeData = useMemo(() => {
        return (data.distribuicao_unidade || []).map(item => ({
            name: (item.unidade || 'un').toUpperCase(),
            value: item.quantidade || 0,
            valor: item.valor || 0
        }));
    }, [data.distribuicao_unidade]);

    // Dados para gráfico de barras por origem
    const origemData = useMemo(() => {
        const orig = data.movimentacoes_por_origem || {};
        return [
            { name: t('stock.originItems'), value: orig.itens || 0 },
            { name: t('stock.originRawMaterials'), value: orig.materias_primas || 0 },
            { name: t('stock.originAdditionals'), value: orig.adicionais || 0 }
        ].filter(item => item.value > 0);
    }, [data.movimentacoes_por_origem, t]);

    const formatCurrency = (value) => formatPriceWithSymbol(value || 0);

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div style={{
                    background: 'white',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    border: '1px solid #e0e0e0'
                }}>
                    <p style={{ margin: 0, fontWeight: 600, color: '#333' }}>{label || payload[0]?.payload?.name}</p>
                    {payload.map((entry, index) => (
                        <p key={index} style={{ margin: '4px 0 0', color: entry.color || '#318CD5', fontSize: '13px' }}>
                            {entry.name}: {entry.dataKey === 'valor' || entry.dataKey === 'custoConsumido'
                                ? formatCurrency(entry.value)
                                : entry.value?.toLocaleString?.() || entry.value}
                        </p>
                    ))}
                </div>
            );
        }
        return null;
    };

    return (
        <>
            <PermissionGate permissions={['default']}>
                <Container sidebar={sidebar}>
                    <Header>
                        <div className="page-title-with-icon">
                            <div className="page-icon"><MdInventory /></div>
                            <h1 className="page-title">{t('stock.title')}</h1>
                        </div>
                        <div className="filters" style={{ marginRight: '50px' }}>
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                            />
                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                            />
                            <button onClick={fetchData} disabled={isLoading}>
                                {isLoading ? t('common.updating') : t('common.update')}
                            </button>
                        </div>
                    </Header>

                    {isLoading ? (
                        <LoadingContainer>
                            <Loading type="spin" color="rgb(49, 140, 213)" height={40} width={40} />
                        </LoadingContainer>
                    ) : (
                        <>
                            {/* === CARDS RESUMO === */}
                            <CardsGrid>
                                <MetricCard>
                                    <div className="icon blue"><FaWarehouse /></div>
                                    <span className="label">{t('stock.totalRawMaterials')}</span>
                                    <span className="value">{data.resumo?.total_materias_primas || 0}</span>
                                    <span className="sub">{t('stock.registered')}</span>
                                </MetricCard>

                                <MetricCard>
                                    <div className="icon green"><MdInventory /></div>
                                    <span className="label">{t('stock.stockValue')}</span>
                                    <span className="value">{formatCurrency(data.resumo?.valor_total_estoque)}</span>
                                    <span className="sub">{t('stock.currentValue')}</span>
                                </MetricCard>

                                <MetricCard>
                                    <div className="icon blue"><FaBoxOpen /></div>
                                    <span className="label">{t('stock.itemsWithStock')}</span>
                                    <span className="value">{data.resumo?.itens_com_estoque || 0}</span>
                                    <span className="sub">{t('stock.itemsControlled')}</span>
                                </MetricCard>

                                <MetricCard>
                                    <div className="icon orange"><FaExclamationTriangle /></div>
                                    <span className="label">{t('stock.lowStock')}</span>
                                    <span className="value">{data.resumo?.mp_abaixo_minimo || 0}</span>
                                    <span className="sub">{t('stock.belowMinimum')}</span>
                                </MetricCard>

                                <MetricCard>
                                    <div className="icon red"><FaTimesCircle /></div>
                                    <span className="label">{t('stock.outOfStock')}</span>
                                    <span className="value">{(data.resumo?.mp_esgotadas || 0) + (data.resumo?.itens_esgotados || 0)}</span>
                                    <span className="sub">{t('stock.mpAndItems')}</span>
                                </MetricCard>

                                <MetricCard>
                                    <div className="icon green"><FaArrowUp /></div>
                                    <span className="label">{t('stock.totalEntries')}</span>
                                    <span className="value">{data.resumo?.total_entradas || 0}</span>
                                    <span className="sub">{t('common.inPeriod')}</span>
                                </MetricCard>

                                <MetricCard>
                                    <div className="icon red"><FaArrowDown /></div>
                                    <span className="label">{t('stock.manualExits')}</span>
                                    <span className="value">{data.resumo?.total_saidas_manuais || 0}</span>
                                    <span className="sub">{t('stock.wasteAndLoss')}</span>
                                </MetricCard>

                                <MetricCard>
                                    <div className="icon purple"><FaSyncAlt /></div>
                                    <span className="label">{t('stock.movements')}</span>
                                    <span className="value">{data.resumo?.total_movimentacoes || 0}</span>
                                    <span className="sub">{t('common.inPeriod')}</span>
                                </MetricCard>
                            </CardsGrid>

                            {/* === GRÁFICOS LINHA 1: Evolução + Distribuição Movimentações === */}
                            <ChartsRow>
                                <FullWidthChart>
                                    <h3><MdTrendingDown /> {t('stock.movementEvolution')}</h3>
                                    <div className="chart-container">
                                        {evolucaoData.length > 0 ? (
                                            <ResponsiveContainer width="100%" height="100%">
                                                <AreaChart data={evolucaoData}>
                                                    <CartesianGrid strokeDasharray="3 3" />
                                                    <XAxis dataKey="data" fontSize={12} />
                                                    <YAxis fontSize={12} />
                                                    <Tooltip content={<CustomTooltip />} />
                                                    <Legend />
                                                    <Area type="monotone" dataKey="entradas" name={t('stock.entries')} stroke={MOVEMENT_COLORS.entradas} fill={MOVEMENT_COLORS.entradas} fillOpacity={0.3} />
                                                    <Area type="monotone" dataKey="saidas" name={t('stock.exits')} stroke={MOVEMENT_COLORS.saidas} fill={MOVEMENT_COLORS.saidas} fillOpacity={0.3} />
                                                    <Area type="monotone" dataKey="saidas_automaticas" name={t('stock.autoExits')} stroke={MOVEMENT_COLORS.saidas_automaticas} fill={MOVEMENT_COLORS.saidas_automaticas} fillOpacity={0.3} />
                                                    <Area type="monotone" dataKey="ajustes" name={t('stock.adjustments')} stroke={MOVEMENT_COLORS.ajustes} fill={MOVEMENT_COLORS.ajustes} fillOpacity={0.3} />
                                                </AreaChart>
                                            </ResponsiveContainer>
                                        ) : (
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#a0aec0' }}>
                                                <p>{t('common.noData')}</p>
                                            </div>
                                        )}
                                    </div>
                                </FullWidthChart>
                            </ChartsRow>

                            {/* === GRÁFICOS LINHA 2: Pizza Movimentações + Top Consumidas === */}
                            <ChartsRow>
                                <ChartCard>
                                    <h3><FaClipboardList /> {t('stock.movementDistribution')}</h3>
                                    <div className="chart-container">
                                        {movPieData.length > 0 ? (
                                            <ResponsiveContainer width="100%" height="100%">
                                                <PieChart>
                                                    <Pie
                                                        data={movPieData}
                                                        cx="50%"
                                                        cy="50%"
                                                        outerRadius={100}
                                                        dataKey="value"
                                                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                                    >
                                                        {movPieData.map((entry, index) => (
                                                            <Cell key={index} fill={Object.values(MOVEMENT_COLORS)[index % 4]} />
                                                        ))}
                                                    </Pie>
                                                    <Tooltip content={<CustomTooltip />} />
                                                    <Legend />
                                                </PieChart>
                                            </ResponsiveContainer>
                                        ) : (
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#a0aec0' }}>
                                                <p>{t('common.noData')}</p>
                                            </div>
                                        )}
                                    </div>
                                </ChartCard>

                                <ChartCard>
                                    <h3><FaBoxOpen /> {t('stock.movementsByOrigin')}</h3>
                                    <div className="chart-container">
                                        {origemData.length > 0 ? (
                                            <ResponsiveContainer width="100%" height="100%">
                                                <BarChart data={origemData}>
                                                    <CartesianGrid strokeDasharray="3 3" />
                                                    <XAxis dataKey="name" fontSize={12} />
                                                    <YAxis fontSize={12} />
                                                    <Tooltip content={<CustomTooltip />} />
                                                    <Bar dataKey="value" name={t('stock.movements')} radius={[4, 4, 0, 0]}>
                                                        {origemData.map((entry, index) => (
                                                            <Cell key={index} fill={Object.values(ORIGIN_COLORS)[index % 3]} />
                                                        ))}
                                                    </Bar>
                                                </BarChart>
                                            </ResponsiveContainer>
                                        ) : (
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#a0aec0' }}>
                                                <p>{t('common.noData')}</p>
                                            </div>
                                        )}
                                    </div>
                                </ChartCard>
                            </ChartsRow>

                            {/* === TABELA: Top Matérias-Primas Consumidas === */}
                            <DataTable>
                                <div className="table-header">
                                    <h3><FaArrowDown style={{ color: '#dc3545' }} /> {t('stock.topConsumedTitle')}</h3>
                                </div>
                                <div className="table-wrapper">
                                    {(data.top_consumidas || []).length > 0 ? (
                                        <table>
                                            <thead>
                                                <tr>
                                                    <th>#</th>
                                                    <th>{t('stock.name')}</th>
                                                    <th>{t('stock.type')}</th>
                                                    <th>{t('stock.consumed')}</th>
                                                    <th>{t('stock.unit')}</th>
                                                    <th>{t('stock.costConsumed')}</th>
                                                    <th>{t('stock.currentStock')}</th>
                                                    <th>{t('stock.movementsCount')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(data.top_consumidas || []).map((item, index) => (
                                                    <tr key={item.id || index}>
                                                        <td>{index + 1}</td>
                                                        <td style={{ fontWeight: 600 }}>{item.nome}</td>
                                                        <td>
                                                            <span className={`status-badge ${item.origem === 'item' ? 'success' : 'warning'}`} style={{ fontSize: '11px' }}>
                                                                {item.origem === 'item' ? t('stock.typeItem') : t('stock.typeRawMaterial')}
                                                            </span>
                                                        </td>
                                                        <td className="valor">{item.quantidade?.toLocaleString?.() || 0}</td>
                                                        <td>{(item.unidadeMedida || 'un').toUpperCase()}</td>
                                                        <td className="valor">{formatCurrency(item.custoConsumido)}</td>
                                                        <td>
                                                            <span className={`status-badge ${item.estoqueAtual === 0 ? 'danger' : item.estoqueAtual <= 5 ? 'warning' : 'success'}`}>
                                                                {item.estoqueAtual?.toLocaleString?.() || 0}
                                                            </span>
                                                        </td>
                                                        <td>{item.movimentacoes}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    ) : (
                                        <div className="empty-state">
                                            <FaBoxOpen />
                                            <p>{t('stock.noConsumption')}</p>
                                        </div>
                                    )}
                                </div>
                            </DataTable>

                            {/* === TABELA: Alertas de Estoque === */}
                            {(data.alertas || []).length > 0 && (
                                <DataTable>
                                    <div className="table-header">
                                        <h3><FaExclamationTriangle style={{ color: '#ff9800' }} /> {t('stock.alertsTitle')}</h3>
                                    </div>
                                    <div className="table-wrapper">
                                        <table>
                                            <thead>
                                                <tr>
                                                    <th>{t('stock.materialName')}</th>
                                                    <th>{t('stock.currentStock')}</th>
                                                    <th>{t('stock.minimum')}</th>
                                                    <th>{t('stock.unit')}</th>
                                                    <th>{t('stock.status')}</th>
                                                    <th>{t('stock.percentOfMin')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(data.alertas || []).map((alerta, index) => (
                                                    <tr key={alerta._id || index}>
                                                        <td style={{ fontWeight: 600 }}>{alerta.nome}</td>
                                                        <td className="valor">{alerta.quantidade?.toLocaleString?.() || 0}</td>
                                                        <td>{alerta.limiteMinimo}</td>
                                                        <td>{(alerta.unidadeMedida || 'un').toUpperCase()}</td>
                                                        <td>
                                                            <span className={`status-badge ${alerta.tipo === 'sem_estoque' ? 'danger' : 'warning'}`}>
                                                                {alerta.tipo === 'sem_estoque' ? t('stock.depleted') : t('stock.low')}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span className={`status-badge ${alerta.percentual <= 25 ? 'danger' : 'warning'}`}>
                                                                {alerta.percentual}%
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </DataTable>
                            )}

                            {/* === TABELA: Custo de Matéria-Prima por Item === */}
                            <DataTable>
                                <div className="table-header">
                                    <h3><IoNutrition style={{ color: '#667eea' }} /> {t('stock.costPerItemTitle')}</h3>
                                </div>
                                <div className="table-wrapper">
                                    {(data.custos_por_item || []).length > 0 ? (
                                        <table>
                                            <thead>
                                                <tr>
                                                    <th>#</th>
                                                    <th>{t('stock.itemName')}</th>
                                                    <th>{t('common.category')}</th>
                                                    <th>{t('stock.ingredients')}</th>
                                                    <th>{t('stock.recipeCost')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(data.custos_por_item || []).map((item, index) => (
                                                    <tr key={index}>
                                                        <td>{index + 1}</td>
                                                        <td style={{ fontWeight: 600 }}>{item.titulo}</td>
                                                        <td>{item.categoria}</td>
                                                        <td>{item.quantidadeIngredientes}</td>
                                                        <td className="valor">{formatCurrency(item.custoTotal)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    ) : (
                                        <div className="empty-state">
                                            <IoNutrition />
                                            <p>{t('stock.noRecipes')}</p>
                                        </div>
                                    )}
                                </div>
                            </DataTable>

                            {/* === TABELA: Itens Esgotados === */}
                            {(data.itens_esgotados || []).length > 0 && (
                                <DataTable>
                                    <div className="table-header">
                                        <h3><FaTimesCircle style={{ color: '#dc3545' }} /> {t('stock.outOfStockItemsTitle')}</h3>
                                    </div>
                                    <div className="table-wrapper">
                                        <table>
                                            <thead>
                                                <tr>
                                                    <th>{t('stock.itemName')}</th>
                                                    <th>{t('common.category')}</th>
                                                    <th>{t('stock.currentStock')}</th>
                                                    <th>{t('stock.statusDelivery')}</th>
                                                    <th>{t('stock.statusDineIn')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(data.itens_esgotados || []).map((item, index) => (
                                                    <tr key={index}>
                                                        <td style={{ fontWeight: 600 }}>{item.titulo}</td>
                                                        <td>{item.categoria}</td>
                                                        <td>{item.quantidadeEstoque}</td>
                                                        <td>
                                                            <span className={`status-badge ${item.out ? 'danger' : 'success'}`}>
                                                                {item.out ? t('stock.unavailable') : t('stock.available')}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span className={`status-badge ${item.out_salao ? 'danger' : 'success'}`}>
                                                                {item.out_salao ? t('stock.unavailable') : t('stock.available')}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </DataTable>
                            )}
                        </>
                    )}
                </Container>
            </PermissionGate>
        </>
    );
};

export default RelatorioEstoque;
